import React, { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import SegmentedTabs, { SegmentedTabItem } from '../../components/SegmentedTabs';
import { paths } from '../../routes';
import Permission from '../../common/permissions';
import { makeHasPermissionSelector } from '../../selector/authenticationSelector';
import OrganizationsTab from './components/OrganizationsTab';
import IndividualCustomersTab from './components/IndividualCustomersTab';
import CustomerClassesTab from './components/CustomerClassesTab';

type CustomerTab = 'organizations' | 'individuals' | 'classes';

const TAB_PARAM = 'tab';
const DEFAULT_TAB: CustomerTab = 'organizations';

const isCustomerTab = (value: string | null): value is CustomerTab =>
    value === 'organizations' || value === 'individuals' || value === 'classes';

/**
 * Customers — the tenant's customer base (H-3b). "Customer" is the umbrella
 * term: it covers customer *organizations* (Verein / company / public body)
 * and individual customers who hold an account without belonging to one. Both
 * live here as tabs so operators have a single place to look, mirroring
 * locaboo's top-level `Kunde` primitive
 * (`docs/research/booking-platform-competitor-org-membership-models.md`).
 *
 * A third tab — the H-3c approval inbox for membership self-claims — was
 * removed on 2026-08-20 with the flow that fed it (ADR-0021). Nothing creates
 * a `pending` membership any more, so the tab could only ever have been empty.
 * Members are managed on the organization itself.
 *
 * The active tab lives in the URL (`?tab=organizations|individuals`) so a
 * refresh or a deep link restores the view the operator was on (house rule:
 * restore selection state from the URL); an unknown or missing value falls
 * back to the organizations tab.
 */
const CustomersPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // The organisation import writes organisations (customers:write), like its route.
    const canWrite = useSelector(makeHasPermissionSelector(Permission.writeCustomers()));
    const tabParam = searchParams.get(TAB_PARAM);
    const activeTab: CustomerTab = isCustomerTab(tabParam) ? tabParam : DEFAULT_TAB;

    const handleTabChange = useCallback(
        (key: CustomerTab) => {
            // Derive the next params from the current ones so other query
            // params survive the switch; `replace` keeps tab toggling out of
            // the history stack.
            const next = new URLSearchParams(searchParams);
            next.set(TAB_PARAM, key);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const tabs: SegmentedTabItem<CustomerTab>[] = [
        { key: 'organizations', label: t('customers.tabs.organizations') },
        { key: 'individuals', label: t('customers.tabs.individuals') },
        // #1808: the segments a tariff can distinguish. Here rather than in
        // Account settings because a class is a property OF the customer base
        // — it is assigned on an organization two tabs away, and the operator
        // who renames one is the operator who assigns it.
        { key: 'classes', label: t('customers.tabs.classes') },
    ];

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>{t('customers.title')}</PageHeadline>
                </div>
                {activeTab === 'organizations' && canWrite && (
                    <CustomButton
                        title={t('organization_import.open')}
                        onClick={() => navigate(paths.customerOrganizationImport)}
                        isFilled={false}
                    />
                )}
                {activeTab === 'organizations' && (
                    <CustomButton
                        title={t('organization.list.create')}
                        onClick={() => navigate(paths.customerOrganizationCreate)}
                        isFilled={true}
                    />
                )}
            </PageHeader>

            <SegmentedTabs items={tabs} value={activeTab} onChange={handleTabChange} />

            {activeTab === 'organizations' && <OrganizationsTab />}
            {activeTab === 'individuals' && <IndividualCustomersTab />}
            {activeTab === 'classes' && <CustomerClassesTab />}
        </div>
    );
};

export default CustomersPage;
